import React, { Fragment, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Styles
import '../form/form.scss';

// Components
import Button from '../button/button';
import MoveList from '../students-lists/move-list';
import { COPY_SELECT_VALUE, MOVE_SELECT_VALUE, REMOVE_SELECT_VALUE } from '../students-lists/move-list-body';
import GenericModal, { MODAL_TYPE } from '../generic-modal/generic-modal';

// Context
import ConsoleModalContext from '../../containers/console/context/console-modal-context';

// Actions
import consoleActions from '../../containers/console/console-actions';

// Action Type for moving students
import { actionTypeIds } from '../../helpers/enums';

function MoveStudents(props) {
    const { i18n } = useLocalisation();

    // Props
    const {
        classGroupStudents,
        mainSelectedStudentIds,
        closeHandler,
        // Actions
        updateCurrentClassId,
        assignClassStudent,
        // State
        productId,
        currentClassId
    } = props;

    // State variables
    const [bottomErrorMessage, setBottomErrorMessage] = useState(null);
    const [studentsToUpdate, setStudentsToUpdate] = useState({});
    const [isApplyButtonDisabled, setIsApplyButtonDisabled] = useState(false);
    const [selectedStudents, setSelectedStudents] = useState([]);

    // ComponentDidMount
    useEffect(() => {
        const newStudents = classGroupStudents.filter(
            student => mainSelectedStudentIds.includes(student.userProfileId)
        );
        setSelectedStudents(newStudents);
    }, [classGroupStudents, mainSelectedStudentIds]);

    // Context
    const { setShowConsoleModal, setConsoleModalContent } = useContext(ConsoleModalContext);

    // Handlers
    const closeConfirmationModal = () => {
        setShowConsoleModal(false);
        closeHandler();
    };

    // This handler will determine which type of student requires to be updated based on their actionType
    const updateMoveStudentList = ({ userId, actionType, actionProperties, isForAllStudents }) => {
        const tempUpdateStudents = {
            ...studentsToUpdate
        };
        if (isForAllStudents) {
            if (actionType === REMOVE_SELECT_VALUE) {
                mainSelectedStudentIds.map((studentId) => {
                    tempUpdateStudents[studentId] = {
                        sourceClassId: currentClassId,
                        studentProfileId: studentId,
                        actionType: actionTypeIds[actionType]
                    };
                    return true;
                });
            } else if ((actionType === COPY_SELECT_VALUE || actionType === MOVE_SELECT_VALUE) && actionProperties) {
                mainSelectedStudentIds.map((studentId) => {
                    tempUpdateStudents[studentId] = {
                        destinationClassId: parseInt(actionProperties.destinationClassId, 10),
                        destinationGroupId: parseInt(actionProperties.destinationGroupId, 10),
                        sourceClassId: currentClassId,
                        studentProfileId: studentId,
                        actionType: actionTypeIds[actionType]
                    };
                    return true;
                });
            } else {
                // If nothing is applicable, clean the list of students (will disable apply button)
                for (const student in tempUpdateStudents) {
                    delete tempUpdateStudents[student];
                }
            }

            // Following `else if` rules apply to NOT ALL STUDENTS.
        } else if (actionType === REMOVE_SELECT_VALUE) {
            tempUpdateStudents[userId] = {
                sourceClassId: currentClassId,
                studentProfileId: userId,
                actionType: actionTypeIds[actionType]
            };
        } else if ((actionType === COPY_SELECT_VALUE || actionType === MOVE_SELECT_VALUE) && actionProperties) {
            tempUpdateStudents[userId] = {
                destinationClassId: parseInt(actionProperties.destinationClassId, 10),
                destinationGroupId: parseInt(actionProperties.destinationGroupId, 10),
                sourceClassId: currentClassId,
                studentProfileId: userId,
                actionType: actionTypeIds[actionType]
            };
        } else if (actionType === '' || actionProperties === undefined) {
            // if the actionType is empty or the property itself is undefined
            delete tempUpdateStudents[userId];
        }

        setStudentsToUpdate(tempUpdateStudents);
    };

    const saveHandler = async (e) => {
        /**
         * TODO: as the Back-end handles all the different actions to perform from one single set of data,
         * TODO: and only a single response is sent, what can happen is: if part of the actions are successful but other
         * TODO: parts are failing, the back-end response will be "failure". Which is misleading as EVERYTHING hasn't
         * TODO: failed.
         *
         * TODO: Back-end should report what is successful and what isn't so that Front-End can display accurate
         * TODO: information.
         * BA 2020-07-01 (accurate as to release 1.16.0)
         */
        // Prevent redirect from the default behaviour of a form
        e.preventDefault();
        setBottomErrorMessage(false);

        const listOfStudentsToUpdate = Object.keys(studentsToUpdate);
        const assignStudents = listOfStudentsToUpdate.map(student => studentsToUpdate[student]);
        const assignStudentsBody = {
            assignStudents
        };

        const requests = await assignClassStudent(assignStudentsBody);

        // Success/Error handling
        if (requests !== false) {
            setConsoleModalContent((
                <GenericModal
                    title={<Trans id={'Move Modal - Title'} />}
                    closeHandler={closeConfirmationModal}
                    parentClasses="modal--small"
                    type={MODAL_TYPE.floating}>
                    <div className="form__message">
                        <Trans id="Add Modal - Success Message" /> <span className="add-students__tick" />
                    </div>
                    <div className="form__row form__row--buttons">
                        <Button
                            callback={closeConfirmationModal}
                            visibleText={i18n._(t`Button - Ok`)} />
                    </div>
                </GenericModal>
            ));
            updateCurrentClassId({ productId, classId: currentClassId });
        } else {
            setBottomErrorMessage(i18n._(t`Update students - Error message`));
        }
    };

    /**
     * This effect will determine whether the student to update has valid value,
     * and check if the user can apply their changes
     */
    useEffect(() => {
        const listOfStudentsToUpdate = Object.keys(studentsToUpdate);
        setIsApplyButtonDisabled(listOfStudentsToUpdate.length === 0);
    }, [studentsToUpdate]);

    return (
        <Fragment>
            <div className="modal__variable-height">
                <MoveList
                    studentsList={selectedStudents}
                    updateMoveStudentList={updateMoveStudentList}
                />
            </div>
            <Trans id={`Add or Move Modal - note message - product ${productId}`}>
                <p className="form__message">
                    <span className="form__note">Title</span>
                    Message
                </p>
            </Trans>
            <div className="form__row form__row--buttons">
                <Button
                    callback={closeConfirmationModal}
                    visibleText={i18n._(t`Modal - Cancel`)} />
                <Button
                    callback={saveHandler}
                    isBtnDisabled={isApplyButtonDisabled}
                    visibleText={i18n._(t`Move Modal - Apply`)} />
            </div>
            {
                bottomErrorMessage ? (
                    <div className="form__row">
                        <p className="form__error-message form__error-message--bottom">
                            {bottomErrorMessage}
                        </p>
                    </div>
                ) : ''
            }
        </Fragment>
    );
}

MoveStudents.propTypes = {
    closeHandler: PropTypes.func,
    // Action
    updateCurrentClassId: PropTypes.func,
    assignClassStudent: PropTypes.func,
    // State
    productId: PropTypes.number,
    currentClassId: PropTypes.number,
    classGroupStudents: PropTypes.array,
    mainSelectedStudentIds: PropTypes.array
};

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from MessageModalCombinedReducers.
 */
function mapStateToProps(state) {
    const {
        productId,
        classGroupStudents,
        mainSelectedStudentIds,
        currentClassId
    } = state.consoleReducer;

    return {
        productId,
        currentClassId,
        classGroupStudents,
        mainSelectedStudentIds
    };
}

/*
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        updateCurrentClassId: consoleActions.updateCurrentClassId,
        assignClassStudent: consoleActions.assignClassStudent
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(MoveStudents);



// WEBPACK FOOTER //
// ./components/move-students/move-students.js